import { useState, useEffect, useMemo } from 'react';
import { JobFilters as JobFiltersType, Job } from '@/types/job';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Search, Filter, X, SortAsc, SortDesc } from 'lucide-react';

interface JobFiltersProps {
  onFiltersChange: (filters: JobFiltersType) => void;
  totalJobs: number;
  allJobs: Job[];
}

export const JobFilters = ({ onFiltersChange, totalJobs, allJobs }: JobFiltersProps) => {
  const [search, setSearch] = useState('');
  const [location, setLocation] = useState('');
  const [department, setDepartment] = useState('');
  const [sortBy, setSortBy] = useState<'title' | 'company' | 'location' | 'publishDate' | ''>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Extract unique values from all jobs for dropdown options
  const uniqueLocations = useMemo(() => {
    const locations = allJobs
      .map(job => job.location)
      .filter(location => location && location.trim() !== '')
      .filter((location, index, self) => self.indexOf(location) === index)
      .sort();
    return locations;
  }, [allJobs]);

  const uniqueDepartments = useMemo(() => {
    const departments = allJobs
      .map(job => job.department)
      .filter(department => department && department.trim() !== '')
      .filter((department, index, self) => self.indexOf(department) === index)
      .sort();
    return departments;
  }, [allJobs]);


  // Real-time filtering with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      onFiltersChange({
        search: search || undefined,
        location: location || undefined,
        department: department || undefined,
        sortBy: sortBy || undefined,
        sortOrder: sortOrder || undefined,
      });
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [search, location, department, sortBy, sortOrder, onFiltersChange]);

  const clearFilters = () => {
    setSearch('');
    setLocation('');
    setDepartment('');
    setSortBy('');
    setSortOrder('asc');
  };

  const hasActiveFilters = search || location || department || sortBy;
  const activeFilterCount = [search, location, department, sortBy].filter(Boolean).length;

  return (
    <div className="bg-card/30 backdrop-blur-sm rounded-lg border border-border/20 p-4 mb-6">
      {/* Compact Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-primary" />
          <span className="font-medium text-sm">Filter & Sortierung</span>
          {activeFilterCount > 0 && (
            <Badge variant="secondary" className="text-xs">
              {activeFilterCount}
            </Badge>
          )}
        </div>
        
        {/* Compact Sort Controls */}
        <div className="flex items-center gap-2">
          <Select value={sortBy} onValueChange={(value) => setSortBy(value as any)}>
            <SelectTrigger className="w-32 h-8 text-xs">
              <SelectValue placeholder="Sortieren" />
            </SelectTrigger>
            <SelectContent className="z-50">
              <SelectItem value="title">Titel</SelectItem>
              <SelectItem value="company">Unternehmen</SelectItem>
              <SelectItem value="location">Standort</SelectItem>
              <SelectItem value="publishDate">Datum</SelectItem>
            </SelectContent>
          </Select>
          {sortBy && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="px-2 h-8 w-8"
            >
              {sortOrder === 'asc' ? <SortAsc className="w-3 h-3" /> : <SortDesc className="w-3 h-3" />}
            </Button>
          )}
        </div>
      </div>
      
      {/* Compact Filter Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-3 h-3 text-muted-foreground" />
          <Input
            placeholder="Suche..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9 text-sm"
          />
        </div>
        
        <Select value={location} onValueChange={setLocation}>
          <SelectTrigger className="h-9 text-sm">
            <SelectValue placeholder="Standort" />
          </SelectTrigger>
          <SelectContent className="z-50">
            {uniqueLocations.map((loc) => (
              <SelectItem key={loc} value={loc}>
                {loc}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select value={department} onValueChange={setDepartment}>
          <SelectTrigger className="h-9 text-sm">
            <SelectValue placeholder="Bereich" />
          </SelectTrigger>
          <SelectContent className="z-50">
            {uniqueDepartments.map((dept) => (
              <SelectItem key={dept} value={dept}>
                {dept}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      {/* Compact Active Filters */}
      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-1 pt-2 border-t border-border/20">
          {search && (
            <Badge variant="outline" className="gap-1 h-6 text-xs">
              {search.length > 12 ? `${search.substring(0, 12)}...` : search}
              <X className="w-3 h-3 cursor-pointer" onClick={() => setSearch('')} />
            </Badge>
          )}
          {location && (
            <Badge variant="outline" className="gap-1 h-6 text-xs">
              {location}
              <X className="w-3 h-3 cursor-pointer" onClick={() => setLocation('')} />
            </Badge>
          )}
          {department && (
            <Badge variant="outline" className="gap-1 h-6 text-xs">
              {department}
              <X className="w-3 h-3 cursor-pointer" onClick={() => setDepartment('')} />
            </Badge>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground ml-auto"
          >
            Zurücksetzen
          </Button>
        </div>
      )}
    </div>
  );
};